import { useState } from 'react';
import { FileText, Download, FolderArchive, Award, ExternalLink, Store } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

interface Certificate {
    id: string;
    name: string;
    type: 'pdf' | 'zip';
    size: string;
    date: string;
    description: string;
}

const certificates: Certificate[] = [
    {
        id: '1',
        name: 'CHEP Scope 3 Emissions Factor',
        type: 'pdf',
        size: '1.8 MB',
        date: 'Nov 15, 2024',
        description: 'Comprehensive emissions factor documentation for CHEP Scope 3 calculations'
    },
    {
        id: '2',
        name: 'Deforestation-Free Statement',
        type: 'pdf',
        size: '0.9 MB',
        date: 'Nov 10, 2024',
        description: 'Official statement certifying commitment to deforestation-free supply chains'
    },
    {
        id: '3',
        name: 'Brambles Sustainability Review',
        type: 'pdf',
        size: '4.2 MB',
        date: 'Oct 30, 2024',
        description: 'Annual sustainability review and performance report from Brambles'
    },
    {
        id: '4',
        name: 'CHEP Life Cycle Assessment - NA Pallet',
        type: 'pdf',
        size: '3.5 MB',
        date: 'Oct 15, 2024',
        description: 'Detailed life cycle assessment for North American CHEP pallet operations'
    },
    {
        id: '5',
        name: 'Our Sustainability Value Proposition',
        type: 'pdf',
        size: '2.1 MB',
        date: 'Sep 25, 2024',
        description: 'Overview of CHEP sustainability value proposition and circular economy benefits'
    }
];

export function CertificatesSection({
    selectedSupplier,
    onSupplierChange
}: {
    selectedSupplier: string;
    onSupplierChange: (supplier: string) => void;
}) {
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const handleDownload = (certificate: Certificate) => {
        // Create a mock download - in production this would download actual files
        const blob = new Blob([`Mock ${certificate.type.toUpperCase()} file: ${certificate.name}`], {
            type: certificate.type === 'pdf' ? 'application/pdf' : 'application/zip'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${certificate.name}.${certificate.type}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleView = (certificate: Certificate) => {
        // For PDFs, open in new tab (mock functionality)
        if (certificate.type === 'pdf') {
            alert(`Opening ${certificate.name} in new tab...\n\nIn production, this would open the actual PDF file.`);
        } else {
            // For ZIP files, just download
            handleDownload(certificate);
        }
    };

    return (
        <>
            {/* Supplier Selection & Certificates Section */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* Certificates Access */}
                <div className="lg:col-span-8 bg-gradient-to-r from-[#0072CE] to-[#0052a3] rounded-xl shadow-lg p-4 hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg">
                                <Award className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h3 className="text-white text-base mb-0.5">Certificates & Reports</h3>
                                <p className="text-xs text-white/80">
                                    Access official certificates, audit reports, and compliance documents
                                </p>
                            </div>
                        </div>
                        <Button
                            onClick={() => setIsDialogOpen(true)}
                            className="bg-white text-[#0072CE] hover:bg-gray-100 hover:scale-105 transition-all duration-300 shadow-md"
                        >
                            <FileText className="w-4 h-4 mr-2" />
                            Access Certificates
                        </Button>
                    </div>
                </div>

                {/* Supplier Selector */}
                <div className="lg:col-span-4 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl shadow-lg p-4 hover:shadow-xl transition-all duration-300">
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-3">
                            <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg">
                                <Store className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-white text-base mb-0.5">Non-Integrated Suppliers</h3>
                                <p className="text-xs text-white/80">
                                    Select a partner to view potential impact
                                </p>
                            </div>
                        </div>
                        <Select value={selectedSupplier} onValueChange={onSupplierChange}>
                            <SelectTrigger className="bg-white/95 border-white/30 text-gray-900 hover:bg-white transition-all duration-200 shadow-md">
                                <SelectValue placeholder="Select a supplier" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                <SelectItem value="All">All</SelectItem>
                                <SelectItem value="Sunrise Flour Mills">Sunrise Flour Mills</SelectItem>
                                <SelectItem value="SweetGlaze Sugar Co.">SweetGlaze Sugar Co.</SelectItem>
                                <SelectItem value="HappyHen Egg Farms">HappyHen Egg Farms</SelectItem>
                                <SelectItem value="Crave Dairy Distributors">Crave Dairy Distributors</SelectItem>
                                <SelectItem value="BrewMasters Coffee Roasters">BrewMasters Coffee Roasters</SelectItem>
                                <SelectItem value="FreshPack Paper Goods">FreshPack Paper Goods</SelectItem>
                                <SelectItem value="Others">Others</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Certificates Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="max-w-[95vw] lg:max-w-[1400px] max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Award className="w-6 h-6 text-[#0072CE]" />
                            Certificates & Documents
                        </DialogTitle>
                        <DialogDescription>
                            Download official certificates, reports, and compliance documentation
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 mt-4">
                        {certificates.map((cert) => (
                            <div
                                key={cert.id}
                                className="border border-gray-200 rounded-lg p-4 hover:border-[#0072CE] hover:shadow-md transition-all duration-200 group"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                        {/* Icon */}
                                        <div className={`p-2 rounded-lg flex-shrink-0 ${cert.type === 'pdf'
                                            ? 'bg-red-50 text-red-600 group-hover:bg-red-100'
                                            : 'bg-purple-50 text-purple-600 group-hover:bg-purple-100'
                                            }`}>
                                            {cert.type === 'pdf' ? (
                                                <FileText className="w-5 h-5" />
                                            ) : (
                                                <FolderArchive className="w-5 h-5" />
                                            )}
                                        </div>

                                        {/* Details */}
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-gray-900 mb-1 truncate">{cert.name}</h4>
                                            <p className="text-sm text-gray-600 mb-2">{cert.description}</p>
                                            <div className="flex items-center gap-3 text-xs text-gray-500">
                                                <span className="uppercase px-2 py-1 bg-gray-100 rounded">
                                                    {cert.type}
                                                </span>
                                                <span>{cert.size}</span>
                                                <span>{cert.date}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {cert.type === 'pdf' && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleView(cert)}
                                                className="hover:bg-blue-50 hover:border-[#0072CE] hover:text-[#0072CE] transition-all duration-200"
                                            >
                                                <ExternalLink className="w-4 h-4 mr-1" />
                                                View
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleDownload(cert)}
                                            className="hover:bg-green-50 hover:border-green-600 hover:text-green-600 transition-all duration-200"
                                        >
                                            <Download className="w-4 h-4 mr-1" />
                                            Download
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Footer Info */}
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-gray-700">
                            <strong>Note:</strong> All documents are digitally signed and verified.
                            For questions about these documents, please contact the Environmental Compliance team.
                        </p>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
